import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { CommonDialog, CommonDialogHeader } from '../../components/Dialog';
import { ReturnReceptionService } from '../../api/services/returnReceptionService';
import { showNotification } from '../../store/Reducers/NotificationSnackbarSlice';
import { IOrderReturn } from '../../types/api/response/returnreceptionresponse';

interface Props {
  orderReturn: IOrderReturn | null;
  isOpen: boolean;
  onClose: () => void;
  onResolved: () => void;
}

interface FormValues {
  resolution_remark: string;
  found_barcodes: string[];
}

export const ResolveReturnDialog = ({ orderReturn, isOpen, onClose, onResolved }: Props) => (
  <CommonDialog
    isOpen={isOpen}
    header={
      <CommonDialogHeader
        title={`Resolve shortfall${orderReturn ? ` — ${orderReturn.return_ref}` : ''}`}
        onClose={onClose}
      />
    }
    body={
      orderReturn ? (
        <ResolveForm
          key={orderReturn.return_ref}
          orderReturn={orderReturn}
          onClose={onClose}
          onResolved={onResolved}
        />
      ) : null
    }
    footer={undefined}
  />
);

const ResolveForm = ({
  orderReturn,
  onClose,
  onResolved,
}: {
  orderReturn: IOrderReturn;
  onClose: () => void;
  onResolved: () => void;
}) => {
  const dispatch = useDispatch();
  const shortPackages = orderReturn.packages.filter(
    (p) => p.outcome === 'MISSING' || p.outcome === 'DAMAGED',
  );

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ defaultValues: { resolution_remark: '', found_barcodes: [] } });

  const onSubmit = handleSubmit(async (values) => {
    try {
      await ReturnReceptionService.resolve(orderReturn.return_ref, {
        resolution_remark: values.resolution_remark.trim(),
        found_barcodes: values.found_barcodes.length ? values.found_barcodes : undefined,
      });
      dispatch(
        showNotification({
          message: `Return ${orderReturn.return_ref} resolved.`,
          severity: 'success',
        }),
      );
      onClose();
      onResolved();
    } catch (error: any) {
      const status = error?.response?.status;
      dispatch(
        showNotification({
          message:
            status === 403
              ? 'You do not have permission to resolve returns.'
              : (error?.response?.data?.message ?? 'Failed to resolve return.'),
          severity: 'error',
        }),
      );
    }
  });

  return (
    <Box
      component="form"
      onSubmit={onSubmit}
      noValidate
      sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
    >
      <Typography variant="body2" color="text.secondary">
        Order <strong>{orderReturn.order_number}</strong>. Resolving records the outcome only — it
        does not move money or stock.
      </Typography>

      {shortPackages.length > 0 && (
        <FormControl>
          <FormLabel>Packages that turned up (marked received)</FormLabel>
          <Controller
            name="found_barcodes"
            control={control}
            render={({ field }) => (
              <FormGroup>
                {shortPackages.map((p) => (
                  <FormControlLabel
                    key={p.barcode}
                    control={
                      <Checkbox
                        checked={field.value.includes(p.barcode)}
                        onChange={(e) =>
                          field.onChange(
                            e.target.checked
                              ? [...field.value, p.barcode]
                              : field.value.filter((b) => b !== p.barcode),
                          )
                        }
                      />
                    }
                    label={`${p.barcode} (${p.outcome})`}
                  />
                ))}
              </FormGroup>
            )}
          />
        </FormControl>
      )}

      <FormControl>
        <FormLabel>Resolution remark</FormLabel>
        <TextField
          placeholder="e.g. parcel found in bay 3 / driver settled / written off"
          multiline
          rows={3}
          fullWidth
          error={!!errors.resolution_remark}
          helperText={errors.resolution_remark?.message}
          {...register('resolution_remark', {
            validate: (value) => !!value.trim() || 'Resolution remark is required',
          })}
        />
      </FormControl>

      <Box display="flex" gap={1.5} justifyContent="flex-end" mt={1}>
        <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" color="warning" disabled={isSubmitting}>
          {isSubmitting ? 'Resolving...' : 'Resolve'}
        </Button>
      </Box>
    </Box>
  );
};
